<template>
    <data-table
        class="trucks-table"
        item-key="plate"
        :sticky-head="true"
        :virtualized="true"
        :headers="headers"
        :items="trucks"
        :custom-sort="sortTrucks"
        :item-class="rowClass"
        :no-data-text="__('no_vehicles_founded')"
        :no-columns-text="__('no_columns_selected')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :reorderable="true"
        :removable="true"
        :sort-column-text="__('sort_column')"
        :move-column-text="__('move_column')"
        :remove-column-text="__('hide_column')"
        @click:row="focusTruck"
        @reorder="setColumnsOrder"
        @remove-column="hideColumn"
    >
        <!-- Status -->
        <template #item-status="{ item }">
            <span class="status-dot" :style="{ backgroundColor: statusColor(item) }"></span>
        </template>

        <!-- Plate -->
        <template #item-plate="{ value }">
            <b>{{ value }}</b>
        </template>

        <!-- Custom ID -->
        <template #item-custom_id="{ value }">
            <b>{{ value }}</b>
        </template>

        <!-- Driver activity -->
        <template #item-activity="{ item, fallback }">
            <template v-if="driverActivity(item)">
                <i
                    :class="driverActivity(item).icon"
                    :style="{ color: driverActivity(item).color }"
                ></i>
                <span class="ms-2">{{ __(driverActivity(item).label) }}</span>
            </template>
            <span v-else>{{ fallback }}</span>
        </template>

        <!-- Codriver -->
        <template #item-codriver="{ item }">
            {{ yesNo(item.lastpoint.codriver) }}
        </template>

        <!-- Speed -->
        <template #item-speed="{ item, fallback }">
            {{ withUnit(speed(item), 'km/h', fallback) }}
        </template>

        <!-- Tachograph speed -->
        <template #item-tacho_speed="{ item, fallback }">
            {{ withUnit(tachoSpeed(item), 'km/h', fallback) }}
        </template>

        <!-- Heading -->
        <template #item-heading="{ item, fallback }">
            <template v-if="heading(item) != null">
                <i
                    class="fa-solid fa-arrow-up"
                    :style="{ transform: `rotate(${heading(item)}deg)` }"
                ></i>
                <span class="ms-2">{{ heading(item) }}°</span>
            </template>
            <template v-else>{{ fallback }}</template>
        </template>

        <!-- Altitude -->
        <template #item-altitude="{ item, fallback }">
            {{ withUnit(altitude(item), 'mslm', fallback) }}
        </template>

        <!-- Battery level -->
        <template #item-battery_level="{ item, fallback }">
            <b>{{ withUnit(batteryLevel(item), '%', fallback) }}</b>
        </template>

        <!-- Fuel level -->
        <template #item-fuel_level="{ item, fallback }">
            <b>{{ withUnit(fuelLevel(item), '%', fallback) }}</b>
        </template>

        <!-- Engine RPM -->
        <template #item-rpm_engine="{ item, fallback }">
            <b>{{ withUnit(engineRpm(item), 'rpm', fallback) }}</b>
        </template>

        <!-- Ignition -->
        <template #item-ignition="{ item }">
            {{ yesNo(item.lastpoint.ignition) }}
        </template>

        <!-- Odometer -->
        <template #item-odometer="{ item, fallback }">
            <b>{{ odometer(item) || fallback }}</b>
        </template>

        <!-- GPS odometer -->
        <template #item-gps_odometer="{ item, fallback }">
            <b>{{ gpsOdometer(item) || fallback }}</b>
        </template>

        <!-- Daily distance: its own endpoint, fetched when the row is on screen -->
        <template #item-daily_distance="{ item, fallback }">
            <async-cell :watch-key="item.id" :load="(signal) => dailyDistance(item, signal)">
                <template #default="{ value }">
                    <b>{{ withUnit(value, 'km', fallback) }}</b>
                </template>
            </async-cell>
        </template>

        <!-- GNSS status -->
        <template #item-gnss_status="{ item, fallback }">
            {{ gnssStatus(item) || fallback }}
        </template>

        <!-- GSM signal -->
        <template #item-gsm_signal="{ item, fallback }">
            <template v-if="gsmSignal(item) != null">
                <i
                    :class="[
                        `fa-solid fa-${gsmSignalIcon(item)}`,
                        gsmSignal(item) === 0 ? 'text-gray-darker' : 'text-success'
                    ]"
                ></i>
                <b class="ms-2">{{ gsmSignal(item) }}</b>
            </template>
            <template v-else>{{ fallback }}</template>
        </template>

        <!-- Driver plafond: one request per driver, on screen only -->
        <template #item-daily_driving="{ item, fallback }">
            <async-cell :watch-key="item.id" :load="() => loadPlafond(item, 'daily_driving')">
                <template #default="{ value }">{{ value || fallback }}</template>
            </async-cell>
        </template>

        <template #item-driving_to_next_break="{ item, fallback }">
            <async-cell
                :watch-key="item.id"
                :load="() => loadPlafond(item, 'driving_to_next_break')"
            >
                <template #default="{ value }">{{ value || fallback }}</template>
            </async-cell>
        </template>

        <template #item-residual_commitment="{ item, fallback }">
            <async-cell :watch-key="item.id" :load="() => loadPlafond(item, 'residual_commitment')">
                <template #default="{ value }">{{ value || fallback }}</template>
            </async-cell>
        </template>

        <!-- Address, with copy and "open in Google Maps" actions. The actions
             lead: the address is as long as it is, and buttons that sit after it
             land in a different place in every row. -->
        <template #item-address="{ item }">
            <div class="address-cell">
                <template v-if="hasAddress(item)">
                    <i
                        class="fa-solid fa-copy address-cell__action"
                        v-b-tooltip.hover
                        :title="__('copy_address')"
                        @click.stop="copyAddress(item)"
                    ></i>
                    <i
                        class="fa-solid fa-arrow-up-right-from-square address-cell__action"
                        v-b-tooltip.hover
                        :title="__('open_in_maps')"
                        @click.stop="openInMaps(item)"
                    ></i>
                </template>

                <reverse-geocoding
                    class="address-cell__text"
                    :latLon="[item.lastpoint.lat, item.lastpoint.lng]"
                    :custom_fields="addressFields"
                    :lazy="true"
                    :skeleton="true"
                    :skeleton-lines="1"
                    @resolved="(address) => setAddressResolved(item, address)"
                />
            </div>
        </template>

        <!-- Nearest city: its own cached lookup (city level), so it costs one
             extra request per position. -->
        <template #item-nearest_city="{ item, fallback }">
            <reverse-geocoding
                v-if="hasCoordinates(item)"
                :latLon="[item.lastpoint.lat, item.lastpoint.lng]"
                :custom_fields="cityFields"
                :city_level="true"
                :lazy="true"
                :skeleton="true"
                :skeleton-lines="1"
            />
            <template v-else>{{ fallback }}</template>
        </template>

        <!-- Alerts -->
        <template #item-alerts="{ item }">
            <i
                v-for="alert in vehicleAlerts(item)"
                :key="alert.problem"
                :class="[alert.icon, 'text-danger', 'me-2']"
                v-b-tooltip.hover
                :title="__(alert.problem)"
            ></i>
        </template>

        <!-- Row actions -->
        <template #item-actions="{ item }">
            <div class="actions-cell">
                <golia-button
                    variant="btn-fill dark-blue"
                    icon="fa-route"
                    size="sm"
                    v-b-tooltip.hover
                    :title="__('plan')"
                    @click.stop="openTruckPlanner(item)"
                />
                <golia-button
                    variant="btn-fill dark-blue"
                    icon="fa-chart-mixed"
                    size="sm"
                    v-b-tooltip.hover
                    :title="__('activity')"
                    @click.stop="openTruckActivities(item)"
                />
            </div>
        </template>
    </data-table>
</template>

<script>
import moment from 'moment'
import copy from 'copy-to-clipboard'
import DataTable from '@/components/commons/DataTable.vue'
import AsyncCell from './TrucksTable/cells/AsyncCell.vue'
import { buildVehicleAlerts } from '@/functions/checkVehicleAlerts'
import { sortByKey } from '@/functions/sortTrucks.js'
import { useTruckColumns } from '@/composables/useTruckColumns'
import { useTruckDailyDistance } from '@/composables/useTruckDailyDistance'
import {
    ADDRESS_FIELDS,
    CITY_FIELDS,
    DRIVER_ACTIVITIES,
    GSM_SIGNAL_ICONS,
    formatKilometers
} from './TrucksTable/cells/formats.js'

export default {
    name: 'TrucksTable',
    components: {
        DataTable,
        AsyncCell
    },
    props: {
        trucks: {
            type: Array,
            default: () => []
        },
        // Default order of the list view, used when no column is sorted and as
        // the tie-break when one is.
        sortAction: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            sortBy: null,
            sortDesc: false,
            addressFields: ADDRESS_FIELDS,
            cityFields: CITY_FIELDS,
            // Truck id → true once its address cell has something to show, so
            // the copy and maps buttons wait for it
            addressResolved: {}
        }
    },
    // `headers` is the columns the user picked, as DataTable headers. Nothing
    // is added on top: with every column hidden the table has none at all.
    setup() {
        // Dragging a header or removing a column writes the same setting the
        // settings modal does, so the two always agree.
        const { headers, setOrder, toggle } = useTruckColumns()
        // Cached per vehicle, fetched by the cell that needs it
        const { load: loadDailyDistance } = useTruckDailyDistance()

        return { headers, setColumnsOrder: setOrder, hideColumn: toggle, loadDailyDistance }
    },
    computed: {
        selectedColumns() {
            return this.headers.map((header) => header.value)
        },
        driversPlafond() {
            return this.$store.getters['getDriversPlafondData']
        }
    },
    methods: {
        formatKilometers,

        /**
         * `12` -> `12 km/h`, and the table's `emptyFallbackValue` when there is
         * no reading at all. `0` is a reading, so it is printed as one.
         */
        withUnit(value, unit, fallback) {
            if (value === null || value === undefined || value === '' || Number.isNaN(value)) {
                return fallback
            }

            return `${value} ${unit}`
        },

        // --- Rows -------------------------------------------------------
        rowClass(truck) {
            return truck.on_focus ? 'on-focus' : ''
        },
        focusTruck(truck) {
            this.$emit('focus', truck)
        },
        openTruckPlanner(truck) {
            this.$emit('plan', truck)
        },
        openTruckActivities(truck) {
            this.$emit('activity', truck)
        },

        /**
         * Sorting for DataTable: the chosen column first, `sortAction` as the
         * tie-break, and `sortAction` alone when no column is sorted.
         */
        sortTrucks(trucks, sortKey, sortDesc) {
            const tieBreak =
                this.sortAction.length > 0 ? sortByKey(this.sortAction, this.resolvePath) : null

            if (sortKey) {
                const direction = sortDesc ? -1 : 1

                return [...trucks].sort((a, b) => {
                    const aValue = this.resolvePath(sortKey, a)
                    const bValue = this.resolvePath(sortKey, b)

                    if (aValue < bValue) return -direction
                    if (aValue > bValue) return direction

                    return tieBreak ? tieBreak(a, b) : 0
                })
            }

            return tieBreak ? [...trucks].sort(tieBreak) : trucks
        },

        // --- Cells ------------------------------------------------------
        /**
         * Same rules as `useVehicleStatus`: anything older than an hour counts
         * as inactive, otherwise the colour follows the activity type.
         */
        statusColor(truck) {
            const lastDate = moment(truck.lastpoint?.date).utc(true)
            const isStale = moment().diff(lastDate, 'hours') > 1

            if (isStale) return 'var(--default-color)'

            if (truck.activity_type === 'moving') return 'var(--moving-color)'
            if (truck.activity_type === 'engine_on') return 'var(--engine-on-color)'
            if (truck.activity_type === 'ignition') return 'var(--ignition-color)'
            if (truck.lastpoint?.ignition > 0) return 'var(--ignition-color)'

            return 'var(--default-color)'
        },
        driverActivity(truck) {
            return DRIVER_ACTIVITIES[truck.lastpoint?.activity] || null
        },
        yesNo(value) {
            return value ? this.__('yes') : this.__('no')
        },
        /**
         * When the tachograph speed has a column of its own the GPS speed is
         * shown here, otherwise this column prefers the tachograph value.
         */
        speed(truck) {
            if (this.selectedColumns.includes('tacho_speed')) return truck.lastpoint.speed

            return truck.lastpoint.tacho_speed || truck.lastpoint.speed
        },
        tachoSpeed(truck) {
            return truck.lastpoint.tacho_speed
        },
        // Degrees clockwise from north, also the rotation of the arrow
        heading(truck) {
            return truck.lastpoint.heading
        },
        // Metres above sea level, reported with decimals nobody reads
        altitude(truck) {
            return parseInt(truck.lastpoint.altitude)
        },
        batteryLevel(truck) {
            return truck.lastpoint.telemetry?.battery_level
        },
        fuelLevel(truck) {
            return truck.lastpoint.fuel_level
        },
        // A negative reading is how the sensor says it has nothing to report
        engineRpm(truck) {
            const rpm = truck.lastpoint.engine_rpm

            return rpm >= 0 ? rpm : null
        },
        odometer(truck) {
            return formatKilometers(truck.lastpoint.odometer)
        },
        gpsOdometer(truck) {
            return formatKilometers(truck.lastpoint.gps_odometer)
        },
        gnssStatus(truck) {
            const value = truck.lastpoint?.telemetry?.gnss_status

            return value ? this.__('gnss_status_values.' + value) : ''
        },
        gsmSignal(truck) {
            return truck.lastpoint?.telemetry?.gsm_signal
        },
        gsmSignalIcon(truck) {
            return GSM_SIGNAL_ICONS[this.gsmSignal(truck)]
        },
        vehicleAlerts(truck) {
            return Object.values(buildVehicleAlerts(truck)).filter((alert) => alert)
        },

        // --- Address ----------------------------------------------------
        setAddressResolved(truck, address) {
            this.$set(this.addressResolved, truck.id, !!address)
        },
        // Coordinates alone are not enough: the actions act on the address
        hasAddress(truck) {
            return this.hasCoordinates(truck) && this.addressResolved[truck.id] === true
        },
        hasCoordinates(truck) {
            const { lat, lng } = truck.lastpoint || {}

            return (
                Number.isFinite(Number(lat)) &&
                Number.isFinite(Number(lng)) &&
                (Number(lat) !== 0 || Number(lng) !== 0)
            )
        },
        async copyAddress(truck) {
            // Already resolved for the visible cell, so this comes from the
            // reverse geocoding cache
            const address = await this.$reverseGeocoding({
                lat: truck.lastpoint.lat,
                lng: truck.lastpoint.lng,
                custom_fields: ADDRESS_FIELDS,
                raw: true
            })

            if (!address) return

            copy(address)

            this.$snotify.success(this.__('copied_address'), { position: 'centerTop' })
        },
        openInMaps(truck) {
            const coordinates = encodeURIComponent(`${truck.lastpoint.lat}, ${truck.lastpoint.lng}`)

            window.open(`https://www.google.com/maps/place/${coordinates}`, '_blank', 'noreferrer')
        },

        // --- Driver plafond ---------------------------------------------
        driverId(truck) {
            const driver = truck.lastpoint?.driver

            // Id 1 is the "no driver" record
            return Number(driver?.id) > 1 ? driver.id : null
        },
        dailyDistance(truck, signal) {
            return this.loadDailyDistance(truck.id, signal)
        },
        /**
         * The plafond of this row's driver. The store action caches it for a
         * minute and shares the request between the rows of the same driver,
         * which is also why it is not aborted: the answer is someone else's too.
         */
        async loadPlafond(truck, metric) {
            const driverId = this.driverId(truck)

            if (!driverId) return null

            await this.$store.dispatch('loadDriverPlafondData', {
                vueInstance: this,
                driverId
            })

            return this.plafond(truck, metric)
        },
        plafond(truck, metric) {
            const driverId = this.driverId(truck)

            if (!driverId) return null

            const data = this.driversPlafond?.[driverId]?.data

            if (!data) return '…'

            if (metric === 'daily_driving') return data.view?.dpv5?.dpv5DayDrive?.driveDay
            if (metric === 'residual_commitment') {
                return data.view?.dpv4?.dpv4ResWD?.residualWorkingDay
            }

            // Driving left before the next break, an ISO 8601 duration
            return data.plafond?.drivePlafond
                ? this.formatDuration(data.plafond.drivePlafond)
                : null
        },
        formatDuration(duration) {
            const parsed = moment.duration(duration)
            const hours = Math.floor(parsed.asHours())

            return (hours ? `${hours}h ` : '') + `${parsed.minutes()}m`
        }
    }
}
</script>

<style scoped lang="scss">
.status-dot {
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    vertical-align: middle;
}

.address-cell {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
}

// `ReverseGeocoding` gives itself `width: 100%`; here it has to size itself
// instead, so the column is laid out wide enough for the whole address.
.address-cell .address-cell__text {
    width: auto;
    flex: 0 0 auto;
    white-space: nowrap;
}

.address-cell__action {
    flex: 0 0 auto;
    cursor: pointer;
    color: var(--main-gray--darker);

    &:hover {
        color: var(--dark-blue);
    }
}

.actions-cell {
    display: flex;
    justify-content: flex-start;
    gap: 5px;

    ::v-deep(.btn) {
        font-size: 12px;
        border-radius: 5px;
        padding: 2px 5px;
    }
}

.trucks-table ::v-deep(.on-focus) .data-table__cell {
    background: var(--main-focus);
    border-color: var(--main-focus);
}
</style>
